import { IcebergButton } from '@tradesolution/iceberg-ui-react';
import { NorwegianDatepicker } from 'components/NorwegianDatepicker';
import { Form, Modal } from 'react-bootstrap';
import GrossisterApi from 'services/KjederegisteretAdminApi/GrossisterApi';
import {
  AddOrUpdateGrossistCommand,
  Grossist,
} from 'services/KjederegisteretAdminApi/GrossisterApi/types';
import useForm from 'utils/hooks/useForm';
import useToaster from 'utils/hooks/useToaster';
import HttpErrorHelper from 'utils/HttpErrorHelper';

interface Props {
  grossist: Grossist;
  onClose: () => void;
}

const GrossistEdit = (props: Props) => {
  const toaster = useToaster();
  const grossist = props.grossist;
  const allowSetNedlagtDato = grossist.activeEnhetCount === 0;

  const initialVals: AddOrUpdateGrossistCommand = {
    grossistEnhetId: props.grossist.grossistEnhetId,
    grossistnr: props.grossist.grossistnr,
    kortnavn: props.grossist.kortnavn,
    grossistgruppe: props.grossist.grossistgruppe,
    nedlagtDato: props.grossist.nedlagtdato,
  };

  const validate = (values: AddOrUpdateGrossistCommand) => {
    const err: any = {};
    if (!values.kortnavn) err.kortnavn = 'Kortnavn må fylles ut';
    return err;
  };

  const onSubmit = async (command: AddOrUpdateGrossistCommand) => {
    try {
      const payload: AddOrUpdateGrossistCommand = {
        ...command,
        nedlagtDato: command.nedlagtDato || null,
      };

      await GrossisterApi.updateGrossist(command.grossistEnhetId, payload);
      toaster.success('Suksess', 'Grossist oppdatert!');
      props.onClose();
    } catch (err) {
      toaster.error('Noe gikk galt.', HttpErrorHelper.formatError(err));
    }
  };

  const { loading, touched, errors, values, handleSubmit, handleChange, resetForm } = useForm(
    initialVals,
    validate,
    onSubmit,
  );

  const handleClose = () => {
    resetForm(initialVals);
    props.onClose();
  };

  return (
    <Modal show={!!props.grossist} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Rediger grossist</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Kortnavn</Form.Label>
            <Form.Control
              name="kortnavn"
              type="text"
              value={values.kortnavn}
              onChange={(e) => handleChange('kortnavn', e.target.value.toUpperCase())}
              isInvalid={touched.kortnavn && !!errors.kortnavn}
              isValid={touched.kortnavn && !errors.kortnavn}
            />
            <Form.Control.Feedback type="invalid">{errors.kortnavn}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mt-1">
            <Form.Label>Grossistnr</Form.Label>
            <Form.Control
              name="grossistnr"
              type="number"
              value={props.grossist.grossistnr}
              readOnly
            />
          </Form.Group>

          <Form.Group className="mt-1">
            <Form.Label>Grossistgruppe</Form.Label>
            <Form.Control
              name="grossistgruppe"
              type="text"
              value={values.grossistgruppe || ''}
              onChange={(e) => handleChange('grossistgruppe', e.target.value.toUpperCase())}
            />
          </Form.Group>
          <Form.Group className="mt-1">
            <Form.Label>Nedlagt dato</Form.Label>
            <NorwegianDatepicker
              value={values.nedlagtDato}
              onChange={(date) => {
                console.log('Selected date:', date);
                handleChange('nedlagtDato', date);
              }}
              disabled={!allowSetNedlagtDato}
            />
            {!allowSetNedlagtDato && (
              <Form.Text className="text-danger">
                Aktive enheter må flyttes til en annen grossist før nedleggelse kan gjøres.
              </Form.Text>
            )}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <IcebergButton
            icon="close"
            variant="outline-primary"
            className="col"
            onClick={handleClose}
          >
            Avbryt
          </IcebergButton>

          <IcebergButton
            variant="primary"
            type="submit"
            isLoading={loading}
            className="col"
            icon={'disk'}
            disabled={
              loading || Object.keys(errors).length > 0 || Object.keys(touched).length === 0
            }
          >
            Lagre
          </IcebergButton>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default GrossistEdit;
